import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "@/lib/adminAuth";

export const ADMIN_TEST_IDS = {
  page: "admin-login-page",
  email: "admin-login-email",
  password: "admin-login-password",
  submit: "admin-login-submit",
  error: "admin-login-error",
};

export default function AdminLoginPage() {
  const { admin, ready, login } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && admin) {
    const to = location.state?.from || "/admin";
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid={ADMIN_TEST_IDS.page} className="min-h-[100vh] bg-paper pt-32 pb-24">
      <div className="max-w-[520px] mx-auto px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
          Admin · Waylio dispatch
        </div>
        <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-forest">
          Sign in to <span className="italic font-light">dispatch</span>.
        </h1>
        <p className="mt-4 text-charcoal/70 max-w-md">
          Access all incoming contact + quote submissions in one place.
        </p>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={onSubmit}
          className="mt-10 bg-cream border border-forest/15 p-6 md:p-8"
        >
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
              Email
            </label>
            <input
              data-testid={ADMIN_TEST_IDS.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="w-full bg-transparent border-0 border-b border-forest/30 focus:border-terracotta outline-none py-2 text-forest text-base"
            />
          </div>
          <div className="mt-6">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
              Password
            </label>
            <input
              data-testid={ADMIN_TEST_IDS.password}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-transparent border-0 border-b border-forest/30 focus:border-terracotta outline-none py-2 text-forest text-base"
            />
          </div>

          {error && (
            <div
              data-testid={ADMIN_TEST_IDS.error}
              className="mt-5 text-sm text-terracotta border border-terracotta/30 bg-terracotta/5 px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid={ADMIN_TEST_IDS.submit}
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-forest hover:bg-terracotta text-cream px-6 py-3.5 text-sm font-medium transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" /> Sign in
              </>
            )}
          </button>
        </motion.form>
      </div>
    </main>
  );
}
